import {
  PRICE_PLACEHOLDER,
  useHypotheticalReturnFee,
} from "@babackapp/return-widget-components/hooks/use-hypothetical-return-fee";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { getOrderCurrency } from "@babackapp/return-widget-components/lib/financials.lib";
import { isFirstFeeBearingArticle } from "@babackapp/return-widget-components/lib/return-fee.lib";
import { getLineItemPaidPriceInCents } from "@babackapp/return-widget-components/lib/paid-price.lib";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useParams } from "react-router-dom";
import { useWidgetParams } from "./use-widget-params";

/** Net amount and return fee announced on the bank-transfer (refund) card. */
export function useRefundOption() {
  const t = useCustomText();
  const { orderId, shopId } = useWidgetParams();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { data: order } = usePublicOrder(orderId);
  const draft = useDraftReturn(orderId);
  const orderCurrency = getOrderCurrency(order);

  const draftLineItem = draft?.lineItems.find((li) => li.lineItemId === lineItemId);
  const feeState = useHypotheticalReturnFee("refund", {
    orderId,
    shopId,
    pendingLineItemId: lineItemId,
    pendingReasonId: draftLineItem?.reasonId ?? undefined,
  });

  const isFeeLoading = feeState.status === "loading";
  const isStale = feeState.status === "ready" && feeState.isStale;
  const bearsFee = isFirstFeeBearingArticle(
    draft?.lineItems ?? [],
    lineItemId,
    "refund",
  );
  const fee = isFeeLoading || !bearsFee ? 0 : feeState.fee;

  const gross =
    getLineItemPaidPriceInCents(order, lineItemId) *
    (draftLineItem?.quantity ?? 1);
  const amountText =
    isFeeLoading || isStale
      ? PRICE_PLACEHOLDER
      : formatPrice(Math.max(0, gross - fee), orderCurrency);
  const feeText =
    !isFeeLoading && fee > 0
      ? t("resolution.feeDeducted", { fee: formatPrice(fee, orderCurrency) })
      : null;

  return { amountText, feeText, isFeeLoading, isDisabled: isFeeLoading || isStale };
}
